import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormikInput, Button } from '../UI';
import Loader from '../UI/Loader/Loader';
import { useUserContext } from '../../contexts/UserContext';
import { addContentfulIntegration, getSpecificContentfulIntegration, updateSpecificContentfulIntegration, validateContentfulTokens } from '../../utils/APIs';
import backwardArrow from '../../assets/icons/backward-arrow.svg';
import { NewContentfulIntegrationSchema } from '../../schemas';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { Eye, EyeOff } from 'lucide-react';
const ContentfulIntegration = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { userData, updateContentfulIntegrations } = useUserContext();
  const { content } = useContentfulContext();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState();
  const [showTokens, setShowTokens] = useState({
    content_delivery_access_token: false,
    content_preview_access_token: false,
    cma_token: false,
  });

  const toggleShowToken = (field) => {
    setShowTokens((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  useEffect(() => {
    fetchIntegrationData();
  }, [id, userData]);

  const fetchIntegrationData = async () => {
    try {
      setLoading(true);
      if (id && userData?.selectedOrganization?.org_token) {
        const result = await getSpecificContentfulIntegration(
          userData.token,
          userData.selectedOrganization.org_token,
          id
        );
        if (result.status) {
          setData(result?.data[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching integration data:", error);
    } finally {
      setLoading(false);
    }
  };

  const [isValidating, setIsValidating] = useState(false);
  const [isValidationComplete, setIsValidationComplete] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [validationSuccess, setValidationSuccess] = useState("");

  const resetValidationState = () => {
    setIsValidationComplete(false);
    setValidationSuccess("");
    setValidationError("");
  };

  const handleValidate = async (values, { setSubmitting, setTouched, validateForm }) => {
    try {
      setIsValidating(true);
      setValidationError(""); 

      const allFields = Object.keys(values);
      const touchedFields = allFields.reduce((acc, field) => {
        acc[field] = true;
        return acc;
      }, {});
      setTouched(touchedFields);

      const errors = await validateForm(values);
      if (Object.keys(errors).length > 0) {
        toast.error("Please fix the form errors before validating.");
        setSubmitting(false);
        return;
      }

      const bodyData = {
        space_id: values.space_id,
        cma_token: values.cma_token,
        content_delivery_access_token: values.content_delivery_access_token,
        environment: values.environments?.split(',')[0]?.trim()
      };

      const result = await validateContentfulTokens(userData.token, userData.selectedOrganization.org_token, bodyData);
      
      if (result?.status) {
        setIsValidationComplete(true);
        setValidationSuccess("Integration validated successfully. You can now submit the form.");
        setValidationError("");
      }
    } catch (error) {
      setIsValidationComplete(false);
      setValidationSuccess("");
      setValidationError(error?.message || "Invalid Contentful integration. Please check your inputs.");
    } finally {
      setIsValidating(false);
      setSubmitting(false);
    }
  };

  const handleSubmit = async (values, actions) => {
    if (!isValidationComplete) {
      toast.error("Please validate the integration first before submitting.");
      return;
    }
    
    try {
      setLoading(true);

      const rawIntegrationData = {
        ...values,
        environments: values.environments.split(',').map(env => env.trim()),
      };

      const integrationData = rawIntegrationData;
      let result;
      if (id) {
        result = await updateSpecificContentfulIntegration(
          userData.token,
          userData.selectedOrganization.org_token,
          { integrationId: id, bodyData: integrationData }
        );
      } else {
        result = await addContentfulIntegration(userData.token, {
          organizationId: userData.selectedOrganization.org_token,
          data: integrationData,
        });
      }

      if (result.status) {
        toast.success(content.NEW_CONTENTFUL_CONTENT.success);
        updateContentfulIntegrations();
      }

      setTimeout(() => {
        navigate('/setup/contentful-integrations');
      }, 1000);
    } catch (error) {
      toast.error(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-4 md:px-8 my-6">
      {loading && <Loader />}
      <div className="flex flex-col lg:w-1/2 justify-center items-start">
        <div className="space-y-4 md:space-y-6 w-full">
          <p className="font-medium cursor-pointer text-primary hover:underline flex items-center" onClick={() => navigate('/setup/contentful-integrations')}>
            <img src={backwardArrow} alt="" className="w-5 h-5 mr-2" /> {content.NEW_CONTENTFUL_CONTENT.back_cta_text}
          </p>

          <Formik
            initialValues={{
              name: data?.name || "",
              space_id: data?.space_id || "",
              content_delivery_access_token: data?.content_delivery_access_token || "",
              content_preview_access_token: data?.content_preview_access_token || "",
              cma_token: data?.cma_token || "",
              environments: data?.environments?.join(', ') || "",
            }}
            enableReinitialize
            validationSchema={() => NewContentfulIntegrationSchema(content.NEW_CONTENTFUL_CONTENT)}
            onSubmit={handleSubmit}
          >
            {({ values, validateForm, setTouched, setSubmitting }) => (
              <Form action='#' className="space-y-4">
                <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-4">{content.NEW_CONTENTFUL_CONTENT.title}</h2>
                <p>{content.NEW_CONTENTFUL_CONTENT.description}</p>

                {/* Name */}
                <FormikInput
                  type="text"
                  id="name"
                  name="name"
                  label={content.NEW_CONTENTFUL_CONTENT.formLabels.integration_name}
                  helperText={content.NEW_CONTENTFUL_CONTENT.help_text.integration_name}
                />

                {/* Space ID */}
                <FormikInput
                  type="text"
                  id="space_id"
                  name="space_id"
                  label={content.NEW_CONTENTFUL_CONTENT.formLabels.space_id}
                  helperText={content.NEW_CONTENTFUL_CONTENT.help_text.space_id}
                  onChange={() => resetValidationState()}
                />

                {/* Content Delivery Access Token */}
                <FormikInput
                  type={showTokens.content_delivery_access_token ? "text" : "password"}
                  id="content_delivery_access_token"
                  name="content_delivery_access_token"
                  label={content.NEW_CONTENTFUL_CONTENT.formLabels.content_delivery_access_token}
                  helperText={content.NEW_CONTENTFUL_CONTENT.help_text.content_delivery_access_token}
                  onChange={() => resetValidationState()}
                  suffixIcon={
                    <button
                      type="button"
                      onClick={() => toggleShowToken('content_delivery_access_token')}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showTokens.content_delivery_access_token ? "Hide delivery token" : "Show delivery token"}
                    >
                      {showTokens.content_delivery_access_token ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />

                {/* Content Preview Access Token */}
                <FormikInput
                  type={showTokens.content_preview_access_token ? "text" : "password"}
                  id="content_preview_access_token"
                  name="content_preview_access_token"
                  label={content.NEW_CONTENTFUL_CONTENT.formLabels.content_preview_access_token}
                  helperText={content.NEW_CONTENTFUL_CONTENT.help_text.content_preview_access_token}
                  onChange={() => resetValidationState()}
                  suffixIcon={
                    <button
                      type="button"
                      onClick={() => toggleShowToken('content_preview_access_token')}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showTokens.content_preview_access_token ? "Hide preview token" : "Show preview token"}
                    >
                      {showTokens.content_preview_access_token ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />

                {/* Content Management Access Token */}
                <FormikInput
                  type={showTokens.cma_token ? "text" : "password"}
                  id="cma_token"
                  name="cma_token"
                  label={content.NEW_CONTENTFUL_CONTENT.formLabels.cma_token}
                  helperText={content.NEW_CONTENTFUL_CONTENT.help_text.cma_token}
                  onChange={() => resetValidationState()}
                  suffixIcon={
                    <button
                      type="button"
                      onClick={() => toggleShowToken('cma_token')}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={showTokens.cma_token ? "Hide management token" : "Show management token"}
                    >
                      {showTokens.cma_token ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                />

                {/* Environments */}
                <FormikInput
                  type="text"
                  id="environments"
                  name="environments"
                  label={content.NEW_CONTENTFUL_CONTENT.formLabels.environments}
                  helperText={content.NEW_CONTENTFUL_CONTENT.help_text.environments}
                  onChange={() => resetValidationState()}
                />

                {validationSuccess && (
                  <div className="p-2 text-green-700">
                    ✓ {validationSuccess}
                  </div>
                )}
                {validationError && (
                  <div className="p-2 text-red-700">
                    ✗ {validationError}
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex items-center justify-start mt-6 gap-2">
                  <Button 
                    onClick={() => handleValidate(values, { setSubmitting, setTouched, validateForm })} 
                    type="button" 
                    variant="secondary"
                    disabled={isValidationComplete || isValidating}
                  >
                    {isValidating ? "Validating..." : isValidationComplete ? "Validated" : "Validate"}
                  </Button>

                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={!isValidationComplete || loading}
                    className={!isValidationComplete ? "opacity-50 cursor-not-allowed" : ""}
                  >
                    {loading ? "Submitting..." : (id ? content.NEW_CONTENTFUL_CONTENT.save_cta_text : content.NEW_CONTENTFUL_CONTENT.add_cta_text)}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ContentfulIntegration;
